import React, { createContext, useContext, useState, useEffect } from 'react';
import { app } from './firebase';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';

interface User {
  uid: string;
  email: string;
  role: 'member' | 'admin';
  emailVerified: boolean;
  username?: string;
  mainCharacterName?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
  register: (email: string, password: string, username: string, mainCharacterName: string) => Promise<string | null>;
  promote: (uid: string) => Promise<void>;
  resendVerification: () => Promise<string | null>;
  sendPasswordReset: (email: string, continueUrl?: string) => Promise<string | null>;
  needsVerification: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const auth = getAuth(app);
const db = getFirestore(app);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        const role = userDoc.exists() ? userDoc.data().role : 'member';
        const username = userDoc.exists() ? userDoc.data().username : undefined;
        const mainCharacterName = userDoc.exists() ? userDoc.data().mainCharacterName : undefined;
        setUser({ uid: firebaseUser.uid, email: firebaseUser.email || '', role, emailVerified: firebaseUser.emailVerified, username, mainCharacterName });
        setNeedsVerification(!firebaseUser.emailVerified);
        setLoading(false);
      } else {
        setUser(null);
        setNeedsVerification(false);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (!cred.user.emailVerified) {
        setNeedsVerification(true);
        await signOut(auth);
        return 'Please verify your email address before logging in.';
      }
      setNeedsVerification(false);
      return null;
    } catch (err: any) {
      return err.message;
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  const register = async (email: string, password: string, username: string, mainCharacterName: string) => {
    try {
      if (!mainCharacterName || !mainCharacterName.trim()) {
        return 'Main character name is required.';
      }
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', cred.user.uid), { 
        role: 'member', 
        email, 
        username,
        mainCharacterName: mainCharacterName.trim()
      });
      await sendEmailVerification(cred.user);
      setNeedsVerification(true);
      await signOut(auth);
      return null;
    } catch (err: any) {
      return err.message;
    }
  };

  const resendVerification = async () => {
    try {
      if (auth.currentUser && !auth.currentUser.emailVerified) {
        await sendEmailVerification(auth.currentUser);
        return null;
      }
      return 'No unverified user is currently signed in.';
    } catch (err: any) {
      return err.message;
    }
  };

  const promote = async (uid: string) => {
    await updateDoc(doc(db, 'users', uid), { role: 'admin' });
    if (user && user.uid === uid) {
      setUser({ ...user, role: 'admin' });
    }
  };

  const sendPasswordReset = async (email: string, continueUrl?: string) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: continueUrl || 'https://just-vibes-guild.com/login',
      });
      return null;
    } catch (err: any) {
      return err.message;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, register, promote, resendVerification, sendPasswordReset, needsVerification, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// Edit Mode Context for Admins
interface EditModeContextType {
  editMode: boolean;
  toggleEditMode: () => void;
}
const EditModeContext = createContext<EditModeContextType | undefined>(undefined);

export const EditModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [editMode, setEditMode] = useState(() => {
    const stored = localStorage.getItem('editMode');
    return stored ? JSON.parse(stored) : false;
  });
  const toggleEditMode = () => {
    setEditMode((prev: boolean) => {
      localStorage.setItem('editMode', JSON.stringify(!prev));
      return !prev;
    });
  };
  return (
    <EditModeContext.Provider value={{ editMode, toggleEditMode }}>
      {children}
    </EditModeContext.Provider>
  );
};

export const useEditMode = () => {
  const ctx = useContext(EditModeContext);
  if (!ctx) throw new Error('useEditMode must be used within EditModeProvider');
  return ctx;
}; 